<template>
  <page-template :is-loading="isLoading" :data-exists="contentFilterProfiles !== null">
    <template #pageHeadline>
      {{ $t('contentFilterProfile.listPage.pageHeadline') }}
    </template>
    <template #pageContent>
      <rbz-table
        :columns="columns"
        :table-data="contentFilterProfiles"
        :sticky-header="true"
        class="rbz-list-table"
        data-qa="content-filter-profiles-table"
        :row-buttons="rowButtons"
        :new-button-link="getNewContentFilterProfile()"
        :show-filter-row="true"
        @duplicate-row-button-clicked="duplicateContentFilterProfile"
        @delete-row-button-clicked="openDeleteContentFilterProfileModal"
        @download-data="downloadDoc"
      >
      </rbz-table>
      <git-history
        :api-path="gitAPIPath"
        ref="gitHistory"
        :restore-target-title="`[${entityName.plural}]`"
        @restore-version="loadContentFilterProfiles"
      />
      <rbz-modal-dialog
        :show="showDeleteContentFilterProfileModal"
        title-icon="IconWarning"
        :title="$t('common.operations.delete.modal.title', entityName)"
        :primary-btn-title="$t('common.operations.delete.modal.confirm')"
        @onPrimaryBtnClick="deleteContentFilterProfile"
        :secondary-btn-title="$t('common.operations.delete.modal.cancel')"
        @onSecondaryBtnClick="showDeleteContentFilterProfileModal = false"
        @closeModal="showDeleteContentFilterProfileModal = false"
        size="medium"
      >
        <template #content>
          {{
            $t('common.operations.delete.modal.message', {
              name: selectedContentFilterProfile.name,
            })
          }}
        </template>
      </rbz-modal-dialog>
    </template>
  </page-template>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import { Action, ContentFilterProfile, GlobalFilter, DocumentName, GenericObject } from '@/types';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import RequestsUtils from '@/helpers/RequestsUtils';
import Utils from 'ui-component-library/helpers/Utils';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { useUserStore } from '@/store/UserStore';
import PageTemplate from '@/views/PageTemplate.vue';
import _ from 'lodash';
import GitHistory from '@/components/GitHistory.vue';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import { RbzTableColumnOptions, RbzTableRowButton } from 'ui-component-library/rbz/types';
import { useRouting } from '@/helpers/RoutingUtils';

export default defineComponent({
  name: 'ContentFilterProfilesList',
  setup() {
    const { getEditPath, getNewPath, navigateTo } = useRouting();
    return {
      getEditPath,
      getNewPath,
      navigateTo,
    };
  },
  components: {
    GitHistory,
    PageTemplate,
    RbzModalDialog,
    RbzTable,
  },
  data() {
    return {
      columns: [
        {
          id: 'name',
          title: this.$t('contentFilterProfile.listPage.name'),
          fieldNames: ['name'],
          isSortable: true,
          isSearchable: true,
          cellContentClasses: 'ellipsis',
          classes: 'clickable',
          autoFocus: true,
          linkCallback: (item) => {
            return this.getContentFilterProfilePath(item);
          },
        },
        {
          id: 'restrict-content-type',
          title: this.$t('contentFilterProfile.listPage.restrictContentType'),
          fieldNames: ['content_type'],
          displayFunction: (item: ContentFilterProfile) => {
            const map: GenericObject = {
              json: this.$t('contentFilterProfile.restrictContentTypeValue.json'),
              multipart_form: this.$t(
                'contentFilterProfile.restrictContentTypeValue.multipartForm'
              ),
              url_encoded: this.$t('contentFilterProfile.restrictContentTypeValue.urlEncoded'),
              xml: this.$t('contentFilterProfile.restrictContentTypeValue.xml'),
              graphql: this.$t('contentFilterProfile.restrictContentTypeValue.graphql'),
            };
            return _.map(item['content_type'], (contentType: string) => {
              return map[contentType];
            }).join('\n');
          },
          isSortable: true,
          isSearchable: true,
          classes: 'width-190px',
          cellContentClasses: 'multi-line white-space-pre-line',
        },
        {
          id: 'decoding',
          title: this.$t('contentFilterProfile.decoding'),
          fieldNames: ['decoding'],
          displayFunction: (item: ContentFilterProfile) => {
            if (!item.decoding) {
              return '';
            }
            const displayValues: string[] = [];
            Object.keys(item.decoding).forEach((decodingKey: string) => {
              if (item.decoding[decodingKey as keyof ContentFilterProfile['decoding']]) {
                displayValues.push(decodingKey);
              }
            });
            return displayValues.join('\n');
          },
          isSortable: true,
          isSearchable: true,
          classes: 'width-150px',
          cellContentClasses: 'multi-line white-space-pre-line',
        },
        {
          id: 'action',
          title: this.$t('contentFilterProfile.listPage.action'),
          fieldNames: ['action'],
          displayFunction: (item: GlobalFilter) => {
            const action = _.find(this.actions, (action) => {
              return action.id === item.action;
            });
            return action?.name || '';
          },
          isSortable: true,
          isSearchable: true,
          classes: 'width-200px action-cell',
          cellContentClasses: 'ellipsis',
        },
      ] as RbzTableColumnOptions[],
      rowButtons: [
        {
          id: 'edit',
          tooltip: this.$t('common.operations.edit.tableTooltip'),
          icon: 'IconEdit',
          linkCallback: this.getContentFilterProfilePath,
        },
        {
          id: 'duplicate',
          tooltip: this.$t('common.operations.duplicate.tableTooltip'),
          icon: 'IconDuplicate',
        },
        {
          id: 'delete',
          icon: 'IconDelete',
          disabledCallback: this.isDeleteDisabled,
          tooltipCallback: (contentFilterProfile: ContentFilterProfile) => {
            return this.isDeleteDisabled(contentFilterProfile)
              ? 'Cannot delete protected or referenced content filter profile'
              : 'Delete';
          },
        },
      ] as RbzTableRowButton[],
      contentFilterProfiles: null as ContentFilterProfile[],
      pageLoadingCounter: null,

      showDeleteContentFilterProfileModal: false,
      selectedContentFilterProfile: null,

      actions: [] as Action[],
      referencedIds: [],
    };
  },

  watch: {
    selectedBranch: {
      handler: async function (val, oldVal) {
        if (
          (this.$route.name as string).includes('Content Filter Profiles List') &&
          val &&
          val !== oldVal
        ) {
          this.setPageLoadingStatus(true);
          await this.loadContentFilterProfiles();
          await this.loadActions();
          await this.loadReferencedIds();
          this.setPageLoadingStatus(false);
        }
      },
      immediate: true,
    },
  },

  computed: {
    selectedBranch(): string {
      if (!this.branchesStore.selectedBranchId) {
        this.branchesStore.setSelectedBranch(this.branchesStore.defaultBranch);
      }
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, useUserStore),

    entityName(): DocumentName {
      return {
        plural: this.$t('contentFilterProfile.plural'),
        singular: this.$t('contentFilterProfile.singular'),
      };
    },

    isLoading(): boolean {
      return this.pageLoadingCounter === null || this.pageLoadingCounter > 0;
    },

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    gitAPIPath(): string {
      return `conf/${this.selectedBranch}/content-filter-profiles/versions`;
    },
  },

  methods: {
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    async loadContentFilterProfiles() {
      this.setPageLoadingStatus(true);
      const url = `conf/${this.selectedBranch}/content-filter-profiles`;
      const fieldNames = _.flatMap(this.columns, 'fieldNames');
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url,
        config: { headers: { 'x-fields': `id, ${_.uniq(fieldNames).join(', ')}` } },
      });
      this.contentFilterProfiles = response?.data?.items || [];
      this.setPageLoadingStatus(false);
    },

    async loadReferencedIds() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/security-policies`,
      });
      const securityPolicies = response?.data?.items || [];
      const referencedIds: string[] = [];

      _.forEach(securityPolicies, (securityPolicy) => {
        _.forEach(securityPolicy.map, (location) => {
          referencedIds.push(location['content_filter_profile']);
        });
      });
      this.referencedIds = _.uniq(_.flatten(referencedIds));
      this.setPageLoadingStatus(false);
    },

    async loadActions() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/actions`,
        config: { headers: { 'x-fields': 'id, name' } },
      });
      this.actions = response?.data?.items || [];
      this.setPageLoadingStatus(false);
    },

    isDeleteDisabled(contentFilterProfile: ContentFilterProfile) {
      return (
        this.isContentFilterProfileProtected(contentFilterProfile) ||
        this.isContentFilterProfileReferenced(contentFilterProfile)
      );
    },

    isContentFilterProfileProtected(contentFilterProfile: ContentFilterProfile): boolean {
      return contentFilterProfile.id.startsWith('__');
    },

    isContentFilterProfileReferenced(contentFilterProfile: ContentFilterProfile): boolean {
      return this.referencedIds.includes(contentFilterProfile.id);
    },

    deleteTooltip(contentFilterProfile: ContentFilterProfile): string {
      if (this.isContentFilterProfileProtected(contentFilterProfile)) {
        return this.$t('common.operations.delete.disabledTooltip.protected', this.entityName);
      }
      if (this.isContentFilterProfileReferenced(contentFilterProfile)) {
        return this.$t('common.operations.delete.disabledTooltip.referenced', this.entityName);
      }
      return this.$t('common.operations.delete.tableTooltip');
    },

    getNewContentFilterProfile() {
      return this.getNewPath('content-filter-profiles');
    },

    getContentFilterProfilePath(contentFilterProfile: ContentFilterProfile) {
      return this.getEditPath('content-filter-profiles', contentFilterProfile.id);
    },

    editContentFilterProfile(contentFilterProfile: ContentFilterProfile) {
      this.navigateTo(this.getContentFilterProfilePath(contentFilterProfile));
    },

    async duplicateContentFilterProfile(contentFilterProfile: ContentFilterProfile) {
      this.setPageLoadingStatus(true);
      const getOriginalResponse = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/content-filter-profiles/${contentFilterProfile.id}`,
      });
      const successMessage = this.$t(
        'common.operations.duplicate.messages.success',
        this.entityName
      );
      const failureMessage = this.$t(
        'common.operations.duplicate.messages.failure',
        this.entityName
      );
      if (!getOriginalResponse?.data) {
        Utils.toast({
          message: failureMessage,
          type: 'is-danger',
        });
        return;
      }
      const originalContentFilterProfile = getOriginalResponse.data;
      const contentFilterProfileToAdd = _.cloneDeep(originalContentFilterProfile);
      contentFilterProfileToAdd.id = DatasetsUtils.generateUUID2();
      contentFilterProfileToAdd.name = Utils.generateUniqueEntityName(
        contentFilterProfileToAdd.name,
        _.map(this.contentFilterProfiles, 'name'),
        true
      );
      const url = `conf/${this.selectedBranch}/content-filter-profiles/${contentFilterProfileToAdd.id}`;
      const postNewResponse = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'POST',
        url,
        data: contentFilterProfileToAdd,
        successMessage,
        failureMessage,
      });
      if (postNewResponse) {
        this.editContentFilterProfile(contentFilterProfileToAdd);
      }
      this.setPageLoadingStatus(false);
    },

    async deleteContentFilterProfile() {
      this.setPageLoadingStatus(true);
      const contentFilterProfileId = this.selectedContentFilterProfile.id;
      const successMessage = this.$t('common.operations.delete.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.delete.messages.failure', this.entityName);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'DELETE',
        url: `conf/${this.selectedBranch}/content-filter-profiles/${contentFilterProfileId}`,
        successMessage,
        failureMessage,
      });
      if (response) {
        this.contentFilterProfiles = _.filter(
          this.contentFilterProfiles,
          (contentFilterProfile) => {
            return contentFilterProfile.id !== contentFilterProfileId;
          }
        );
      }
      this.showDeleteContentFilterProfileModal = false;
      this.setPageLoadingStatus(false);
    },

    downloadDoc() {
      Utils.downloadFile('content-filter-profiles', 'json', this.contentFilterProfiles);
    },

    openDeleteContentFilterProfileModal(contentFilterProfile: ContentFilterProfile) {
      this.showDeleteContentFilterProfileModal = true;
      this.selectedContentFilterProfile = contentFilterProfile;
    },
  },
});
</script>
